/**
 * The bootstrap CloudFormation template a customer runs in their own account.
 *
 * This is the trust boundary of the whole product, expressed in about a hundred
 * lines of IAM. It is authored as a typed value rather than a hand-edited YAML
 * file, and pinned by golden fixtures, for the same reason the Terraform config
 * contract is: an accidental change here is a security regression in someone
 * else's account, and it should be a failing test rather than a review someone
 * skims.
 *
 * ## Two roles, deliberately different
 *
 * `QrveyDiagnosticsRole` is read-only and long-lived. It is what support uses day
 * to day, and it is the *only* role the control plane can assume. It cannot read
 * customer data — see the deny statement below, which is the whole point.
 *
 * `QrveyDeployExecRole` is broad, and assumable **only by the Terraform runner
 * inside the customer's own account**. The control plane is not a trusted
 * principal and must never become one: that separation is what stops a
 * compromise of Qrvey's control plane from becoming write access to every
 * customer's infrastructure. The control plane asks the in-account dispatcher to
 * run something; it cannot perform the run itself.
 */
/** Bumped when the template changes in a way the control plane must know about. */
export const BOOTSTRAP_VERSION = '6';
export const DIAGNOSTICS_ROLE_NAME = 'QrveyDiagnosticsRole';
export const DEPLOY_EXEC_ROLE_NAME = 'QrveyDeployExecRole';
/**
 * The in-account Terraform runner's task role.
 *
 * Created by the dispatcher template (IM-26), not this one. Named here because
 * the deploy execution role's trust policy has to refer to it, and the two must
 * agree.
 */
export const RUNNER_ROLE_NAME = 'QrveyTerraformRunner';
/**
 * Terraform state lives in the customer's own account, as the prototype
 * correctly did. What the prototype lacked was **locking**: it set a state key
 * but provisioned no lock table, so two concurrent applies could corrupt state.
 * Recovering corrupted Terraform state in someone else's account is expensive
 * and manual, which is why versioning below is not optional either.
 */
export const LOCK_TABLE_NAME = 'qrvey-terraform-locks';
/**
 * The lock table's partition key.
 *
 * Terraform's S3 backend requires exactly this attribute name — it writes a
 * conditional put on `LockID` and nothing else. A table with any other key
 * schema is accepted at bootstrap and then **silently fails to lock**, which is
 * the failure this story exists to prevent, so it is asserted rather than
 * assumed.
 */
export const LOCK_TABLE_KEY = 'LockID';
/**
 * The Terraform state bucket's name, which is also how Qrvey finds a region.
 *
 * Account- and region-qualified because S3 bucket names are globally unique —
 * and that qualification turns out to carry more weight than uniqueness. Since
 * IM-95 it is how the control plane discovers **which regions an account is set
 * up in**: one `s3:ListAllMyBuckets` call, filtered by this prefix, and the
 * region falls out of the name.
 *
 * That matters because the alternative was a `DescribeStacks` per candidate
 * region — a call per region on a screen already capped at two. One global list
 * costs the same whether a customer has one region or ten.
 *
 * So the shape of this name is load-bearing in two places, and it used to be a
 * literal written out in each template. `regionFromStateBucket` is its inverse,
 * and a test round-trips them so the two cannot drift apart.
 */
export const STATE_BUCKET_PREFIX = 'qrvey-tfstate';
/**
 * The bucket one account's state lives in, in one region.
 *
 * The third caller of this shape, and the reason it is a function rather than a
 * third literal: the templates create the bucket, `regionFromStateBucket` reads
 * a region back out of the name, and the console names the object a withheld
 * secret can be read from. A name assembled by hand in any one of those places
 * is a path that looks right and points nowhere.
 */
export const stateBucketFor = (awsAccountId, region) => `${STATE_BUCKET_PREFIX}-${awsAccountId}-${region}`;
/** As CloudFormation writes it, for a template that creates the bucket. */
export const STATE_BUCKET_NAME_SUB = stateBucketFor('${AWS::AccountId}', '${AWS::Region}');
/**
 * Which region a state bucket belongs to, or undefined if it is not one of ours.
 *
 * Deliberately strict about the account id as well as the prefix: a customer's
 * own bucket called `qrvey-tfstate-something` should not be read as a region
 * Qrvey set up, and `ListAllMyBuckets` returns every bucket they have.
 */
export const regionFromStateBucket = (bucketName, awsAccountId) => {
    const expected = `${STATE_BUCKET_PREFIX}-${awsAccountId}-`;
    if (!bucketName.startsWith(expected))
        return undefined;
    const region = bucketName.slice(expected.length);
    // The same shape `regionProblem` accepts, restated rather than imported to
    // keep this module free of a dependency on the deployment contract.
    return /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/.test(region) ? region : undefined;
};
/** Run logs, in the customer's account. Their record of what was done. */
export const RUN_LOG_GROUP = '/qrvey/terraform-runs';
/** Bounded so versioning does not grow without limit; long enough to recover. */
export const STATE_VERSION_RETENTION_DAYS = 90;
export const RUN_LOG_RETENTION_DAYS = 90;
const sub = (template) => ({ 'Fn::Sub': template });
/**
 * Actions that read customer data rather than describe infrastructure.
 *
 * Denied outright on the diagnostics role. This matters more than the allow list
 * above it: AWS's own `ReadOnlyAccess` policy would have been one line and grants
 * `s3:GetObject`, `dynamodb:Scan` and `secretsmanager:GetSecretValue` — that is
 * read access to the customer's data, which is exactly what this product
 * promises never to touch. An explicit deny cannot be widened by a later
 * accidental allow, since deny always wins.
 *
 * IM-51 extends this; the list here covers the paths that would leak data
 * fastest.
 */
const DATA_PLANE_ACTIONS = [
    's3:GetObject',
    's3:GetObjectVersion',
    'dynamodb:GetItem',
    'dynamodb:BatchGetItem',
    'dynamodb:Query',
    'dynamodb:Scan',
    'secretsmanager:GetSecretValue',
    'kms:Decrypt',
    'rds-data:ExecuteStatement',
    'rds-data:BatchExecuteStatement',
    'es:ESHttpGet',
    'es:ESHttpPost',
    'aoss:APIAccessAll',
    'sqs:ReceiveMessage',
];
/**
 * Parameter Store reads, denied everywhere **except** our own namespace.
 *
 * Split out of the blanket deny above because the two are not the same question.
 * A customer's own parameters routinely hold connection strings and API keys, so
 * reading them is reading customer data. The values *we* publish under
 * `/qrvey/` — the bootstrap version, the state backend — are ours, and the
 * control plane has to read them to know what it is talking to.
 *
 * This was a real bug: IM-24 denied `ssm:GetParameter` outright and IM-25 then
 * published the bootstrap version to SSM. Nothing failed, because nothing read
 * it until IM-27. Deny always wins, so no later allow could have fixed it.
 */
const PARAMETER_READ_ACTIONS = [
    'ssm:GetParameter',
    'ssm:GetParameters',
    'ssm:GetParametersByPath',
];
/**
 * What support may actually do.
 *
 * Describe and list only, and only the services a Qrvey instance is built from.
 * Read-only is not the same as harmless, so this is an allow list rather than
 * `ReadOnlyAccess` minus exceptions — a new AWS service is then invisible to
 * support until someone adds it deliberately, which is the safer default.
 */
const DIAGNOSTICS_ACTIONS = [
    /*
     * What the account spends (IM-130).
     *
     * Cost Explorer reports what AWS billed, aggregated — it reads no resource
     * and can return no customer data, which is why it sits in the allow list
     * rather than needing a carve-out from the data-plane deny the way
     * `ssm:GetParameter` did.
     *
     * It is still commercially sensitive, so it is its own capability on this
     * side (`cost:read`) rather than falling out of `deployment:read`, and every
     * read lands in the customer's own CloudTrail attributed by session name like
     * everything else.
     *
     * `ce:GetCostAndUsage` alone, not `ce:Get*` or `ce:*`: the rest of that
     * namespace includes anomaly monitors, budgets and savings-plan purchase
     * recommendations, none of which this reads and one of which writes.
     */
    'ce:GetCostAndUsage',
    'cloudformation:Describe*',
    'cloudformation:Get*',
    'cloudformation:List*',
    'cloudwatch:Describe*',
    'cloudwatch:Get*',
    'cloudwatch:List*',
    'dynamodb:DescribeTable',
    'dynamodb:ListTables',
    'ec2:Describe*',
    'ecs:Describe*',
    'ecs:List*',
    'eks:Describe*',
    'eks:List*',
    'elasticache:Describe*',
    'elasticloadbalancing:Describe*',
    'es:Describe*',
    'es:List*',
    'iam:Get*',
    'iam:List*',
    'lambda:Get*',
    'lambda:List*',
    'logs:Describe*',
    'logs:FilterLogEvents',
    'logs:GetLogEvents',
    'rds:Describe*',
    'rds:List*',
    's3:GetBucketLocation',
    's3:GetBucketPolicy',
    's3:GetBucketTagging',
    's3:GetEncryptionConfiguration',
    's3:ListAllMyBuckets',
    's3:ListBucket',
    'sqs:GetQueueAttributes',
    'sqs:ListQueues',
    'ssm:DescribeParameters',
    // Permitted broadly here and narrowed by the deny above to /qrvey/* only.
    'ssm:GetParameter',
    'ssm:GetParameters',
    'ssm:GetParametersByPath',
    'sts:GetCallerIdentity',
];
export const buildBootstrapTemplate = (input) => {
    const externalIdParameter = {
        Type: 'String',
        Description: 'Generated by Qrvey and shown when this account was registered. Do not invent a value.',
        MinLength: 2,
        MaxLength: 1224,
        AllowedPattern: '[\\w+=,.@:/-]+',
        // Hides the value in the console, in stack events and in describe-stacks
        // output. A shared secret has no business appearing in either.
        NoEcho: true,
        ...(input.externalId === undefined ? {} : { Default: input.externalId }),
    };
    return {
        AWSTemplateFormatVersion: '2010-09-09',
        Description: `Qrvey Instance Manager access for this account (bootstrap v${BOOTSTRAP_VERSION})`,
        Metadata: {
            'AWS::CloudFormation::Interface': {
                ParameterGroups: [
                    {
                        Label: { default: 'Qrvey access' },
                        Parameters: ['ExternalId', 'ControlPlaneAccountId'],
                    },
                    {
                        Label: { default: 'Networking (optional, for private cluster endpoints)' },
                        Parameters: ['VpcId', 'SubnetIds'],
                    },
                ],
            },
        },
        Parameters: {
            ExternalId: externalIdParameter,
            ControlPlaneAccountId: {
                Type: 'String',
                Description: "Qrvey's account. Only this account may assume the diagnostics role.",
                AllowedPattern: '\\d{12}',
                Default: input.controlPlaneAccountId,
            },
        },
        Resources: {
            DiagnosticsRole: {
                Type: 'AWS::IAM::Role',
                Properties: {
                    RoleName: DIAGNOSTICS_ROLE_NAME,
                    Description: 'Read-only access for Qrvey support. Cannot read customer data.',
                    // One hour. Support sessions are interactive; a longer window is a
                    // longer-lived credential for no benefit.
                    MaxSessionDuration: 3600,
                    AssumeRolePolicyDocument: {
                        Version: '2012-10-17',
                        Statement: [
                            {
                                Effect: 'Allow',
                                // The account root as principal, narrowed by the external ID.
                                // Naming a specific Qrvey role here would couple this template
                                // to Qrvey's internal role naming, and every rename would need
                                // every customer to redeploy.
                                Principal: { AWS: sub('arn:${AWS::Partition}:iam::${ControlPlaneAccountId}:root') },
                                Action: 'sts:AssumeRole',
                                Condition: { StringEquals: { 'sts:ExternalId': { Ref: 'ExternalId' } } },
                            },
                        ],
                    },
                    Policies: [
                        {
                            PolicyName: 'QrveyDiagnosticsRead',
                            PolicyDocument: {
                                Version: '2012-10-17',
                                Statement: [
                                    {
                                        Sid: 'DescribeInfrastructure',
                                        Effect: 'Allow',
                                        Action: [...DIAGNOSTICS_ACTIONS],
                                        Resource: '*',
                                    },
                                    {
                                        Sid: 'NeverReadCustomerData',
                                        Effect: 'Deny',
                                        Action: [...DATA_PLANE_ACTIONS],
                                        Resource: '*',
                                    },
                                    {
                                        // Deny with NotResource: everything except our own
                                        // namespace. Reading /qrvey/* is reading our own published
                                        // facts; reading anything else is reading customer data.
                                        Sid: 'NeverReadCustomerParameters',
                                        Effect: 'Deny',
                                        Action: [...PARAMETER_READ_ACTIONS],
                                        NotResource: sub('arn:${AWS::Partition}:ssm:*:${AWS::AccountId}:parameter/qrvey/*'),
                                    },
                                ],
                            },
                        },
                    ],
                },
            },
            DeployExecRole: {
                Type: 'AWS::IAM::Role',
                Properties: {
                    RoleName: DEPLOY_EXEC_ROLE_NAME,
                    Description: 'Used by the in-account Terraform runner. Qrvey cannot assume this role.',
                    // Terraform applies for a full instance run long; a session that
                    // expires mid-apply leaves half-created infrastructure.
                    MaxSessionDuration: 14400,
                    AssumeRolePolicyDocument: {
                        Version: '2012-10-17',
                        Statement: [
                            {
                                Sid: 'OnlyTheInAccountRunner',
                                Effect: 'Allow',
                                /**
                                 * The *customer's* own account, narrowed by `aws:PrincipalArn`
                                 * to exactly the runner role.
                                 *
                                 * Naming the runner role ARN directly as the principal would be
                                 * equivalent but will not deploy: IAM validates principals and
                                 * rejects an ARN for a role that does not exist yet, and the
                                 * runner is created later by the dispatcher template. Root plus
                                 * a PrincipalArn condition is the standard way around that, and
                                 * it also survives the runner role being recreated.
                                 *
                                 * Note what is absent: Qrvey's account. It must stay absent.
                                 *
                                 * Also absent, since v5: an `sts:ExternalId` condition. An
                                 * external id is confused-deputy protection for a *cross*-account
                                 * trust — it is what stops someone who learns a customer's role
                                 * ARN from inducing Qrvey to assume it on their behalf. That is a
                                 * real threat against `QrveyDiagnosticsRole`, whose trusted
                                 * principal is Qrvey's account, and it is why that role still
                                 * carries one.
                                 *
                                 * This role is different in shape. It is trusted only by
                                 * principals inside the customer's *own* account, narrowed to a
                                 * single role ARN, so there is no deputy to confuse. Exploiting
                                 * the absence means being able to create or modify
                                 * `QrveyTerraformRunner` in that account — at which point you are
                                 * an administrator there and a shared secret protects nothing.
                                 * It was the diagnostics role's requirement copied onto a role
                                 * with a different threat model, and it cost a mandatory NoEcho
                                 * field on two more templates plus a secret in every dispatch.
                                 *
                                 * What gates this role is unchanged, and is where it belongs: the
                                 * runner's own identity policy may assume it only before the
                                 * grant expiry, enforced by AWS at `AssumeRole`.
                                 */
                                Principal: { AWS: sub('arn:${AWS::Partition}:iam::${AWS::AccountId}:root') },
                                Action: 'sts:AssumeRole',
                                Condition: {
                                    ArnEquals: {
                                        'aws:PrincipalArn': sub(`arn:\${AWS::Partition}:iam::\${AWS::AccountId}:role/${RUNNER_ROLE_NAME}`),
                                    },
                                },
                            },
                        ],
                    },
                    // Broad for now, and narrowed empirically in IM-50 by recording what a
                    // real apply actually calls. The blast-radius control at this stage is
                    // that Qrvey cannot assume this role at all, not the permission set.
                    ManagedPolicyArns: [sub('arn:${AWS::Partition}:iam::aws:policy/AdministratorAccess')],
                },
            },
            /**
             * Terraform state.
             *
             * `DeletionPolicy: Retain` because deleting this bucket destroys the state
             * for every deployment in the account, which orphans the infrastructure it
             * describes — Terraform would no longer know the resources exist, and they
             * would have to be imported or deleted by hand. Retaining it does mean a
             * re-bootstrap after a stack delete hits a name conflict; that is the
             * cheaper of the two problems by a wide margin.
             */
        },
        Outputs: {
            DiagnosticsRoleArn: {
                Description: 'Give this to Qrvey to complete onboarding',
                Value: { 'Fn::GetAtt': ['DiagnosticsRole', 'Arn'] },
            },
            DeployExecRoleArn: {
                Description: 'Used by the in-account runner only',
                Value: { 'Fn::GetAtt': ['DeployExecRole', 'Arn'] },
            },
            BootstrapVersion: { Value: BOOTSTRAP_VERSION },
            // Deliberately no external ID output. Outputs are readable by anyone with
            // cloudformation:DescribeStacks, which defeats NoEcho on the parameter.
        },
    };
};
//# sourceMappingURL=template.js.map